import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Space, Tag, Card, Drawer, Form, Input, Select, InputNumber,
  Typography, Tooltip, message, Popconfirm, Row, Col, Divider,
  Timeline, Spin, Empty, Tabs, Table, Collapse, DatePicker, Badge,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, EyeOutlined,
  MinusCircleOutlined, BulbOutlined, ExperimentOutlined, BugOutlined,
  WarningOutlined, DatabaseOutlined, CalendarOutlined, SearchOutlined,
  ThunderboltOutlined, InfoCircleOutlined, NodeExpandOutlined, RobotOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getCropDataListApi, getCropDataByIdApi, createCropDataApi,
  updateCropDataApi, deleteCropDataApi, getCropAdvisoryApi, aiGetCropDataApi,
} from '../../api/cropData';
import { getCropNamesApi } from '../../api/crops';

const { Title, Text } = Typography;
const { Panel } = Collapse;
const PRIMARY = '#1a6b2a';

const SEASON_COLOR  = { kharif: 'orange', rabi: 'blue', zaid: 'green' };
const RISK_COLOR    = { pest: 'red', disease: 'volcano', weather: 'geekblue', other: 'default' };
const ACTION_COLOR  = { irrigation: 'cyan', fertilizer: 'green', pesticide: 'red', harvesting: 'gold', weeding: 'purple', other: 'default' };

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman & Nicobar Islands', 'Chandigarh', 'Dadra & Nagar Haveli', 'Daman & Diu',
  'Delhi', 'Jammu & Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
].map((s) => ({ value: s, label: s }));

const SEASON_OPTIONS      = ['kharif', 'rabi', 'zaid'].map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
const HARVEST_OPTIONS     = [{ value: 'single_day', label: 'Single Day' }, { value: 'multi_day', label: 'Multi Day' }];
const WATER_REQ_OPTIONS   = ['Low', 'Medium', 'High'].map((v) => ({ value: v, label: v }));
const ACTION_TYPE_OPTIONS = ['irrigation', 'fertilizer', 'pesticide', 'harvesting', 'weeding', 'other'].map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
const RISK_TYPE_OPTIONS   = ['pest', 'disease', 'weather', 'other'].map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));

// ── Helpers ──────────────────────────────────────────────────────────────────

function microArrToList(arr) {
  return (arr || []).map((value) => ({ value }));
}

function microListToArr(list) {
  return (list || []).map((item) => item?.value?.trim()).filter(Boolean);
}

// micronutrients arrives as a JSON string from the API e.g. "[\"zinc\",\"iron\"]"
function parseMicronutrients(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
}

function apiErr(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;
}

// ── Advisory Panel ────────────────────────────────────────────────────────────

function AdvisoryPanel({ cropId }) {
  const [sowingDate, setSowingDate] = useState(null);
  const [advisory,   setAdvisory]   = useState(null);
  const [loading,    setLoading]    = useState(false);

  const fetchAdvisory = async () => {
    if (!sowingDate) { message.warning('Select a sowing date first'); return; }
    setLoading(true);
    try {
      const res = await getCropAdvisoryApi(cropId, { sowing_date: sowingDate.format('YYYY-MM-DD') });
      setAdvisory(res.data?.data || null);
    } catch (err) {
      message.error(apiErr(err, 'Failed to load advisory'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card style={{ borderRadius: 10, marginBottom: 14, background: '#f6ffed', border: '1px solid #b7eb8f' }}
        styles={{ body: { padding: '14px 16px' } }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: PRIMARY }}>
          <BulbOutlined style={{ marginRight: 6 }} />Smart Crop Advisory
        </div>
        <Space wrap>
          <DatePicker
            value={sowingDate}
            onChange={setSowingDate}
            format="DD MMM YYYY"
            placeholder="Select sowing date"
            disabledDate={(d) => d && d > dayjs()}
          />
          <Button
            type="primary"
            onClick={fetchAdvisory}
            loading={loading}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
          >
            Get Advisory
          </Button>
        </Space>
      </Card>

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

      {advisory && !loading && (
        <div>
          <div style={{ marginBottom: 12 }}>
            <Tag color={PRIMARY} style={{ fontSize: 13, padding: '4px 10px' }}>
              Day {advisory.current_day}
            </Tag>
            {advisory.stage && (
              <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                Stage: {advisory.stage.stage_name}
              </Tag>
            )}
          </div>

          {advisory.actions?.length > 0 && (
            <Card size="small" style={{ borderRadius: 8, marginBottom: 12 }}
              title={<span style={{ fontSize: 13 }}><ThunderboltOutlined style={{ marginRight: 5, color: PRIMARY }} />Today's Actions</span>}>
              <Timeline
                items={advisory.actions.map((a, i) => ({
                  key: i,
                  color: ACTION_COLOR[a.type] || 'blue',
                  children: (
                    <div>
                      <Tag color={ACTION_COLOR[a.type] || 'default'} style={{ fontSize: 11, textTransform: 'capitalize' }}>{a.type}</Tag>
                      <span style={{ fontWeight: 600, marginLeft: 6 }}>{a.title}</span>
                      {a.product_name && <div style={{ fontSize: 12, color: '#666' }}>Product: {a.product_name}{a.dose_per_hectare ? ` · ${a.dose_per_hectare}/ha` : ''}</div>}
                      {a.instructions && <div style={{ fontSize: 12, color: '#8c8c8c', fontStyle: 'italic' }}>{a.instructions}</div>}
                    </div>
                  ),
                }))}
              />
            </Card>
          )}

          {advisory.risks?.length > 0 && (
            <Card size="small" style={{ borderRadius: 8 }}
              title={<span style={{ fontSize: 13 }}><WarningOutlined style={{ marginRight: 5, color: '#faad14' }} />Watch Out For</span>}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {advisory.risks.map((r, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 10px', background: '#fff7e6', borderRadius: 6, border: '1px solid #ffd591' }}>
                    <Tag color={RISK_COLOR[r.type] || 'default'} style={{ textTransform: 'capitalize', flexShrink: 0 }}>{r.type}</Tag>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name}</div>
                      {r.symptoms && <div style={{ fontSize: 12, color: '#666' }}>Symptoms: {r.symptoms}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ── Detail Drawer ─────────────────────────────────────────────────────────────

function CropDetailDrawer({ open, cropId, onClose, onEdit }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !cropId) return;
    setDetail(null);
    setLoading(true);
    getCropDataByIdApi(cropId)
      .then((res) => setDetail(res.data?.data || null))
      .catch(() => message.error('Failed to load crop details'))
      .finally(() => setLoading(false));
  }, [open, cropId]);

  // API returns: { crop: {...}, stages: [...], risks: [...] }
  const crop    = detail?.crop   || {};
  const stages  = detail?.stages || [];
  const risks   = detail?.risks  || [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 40 }}>
          <Space>
            <span style={{ fontWeight: 700, fontSize: 16 }}>{crop.name || 'Crop Detail'}</span>
            {crop.season && <Tag color={SEASON_COLOR[crop.season] || 'default'} style={{ textTransform: 'capitalize' }}>{crop.season}</Tag>}
          </Space>
          {onEdit && (
            <Button icon={<EditOutlined />} onClick={() => { onClose(); onEdit(cropId); }}>
              Edit
            </Button>
          )}
        </div>
      }
      width={720}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : !detail ? (
        <Empty description="Failed to load crop data" />
      ) : (
        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: 'overview',
              label: <span><InfoCircleOutlined /> Overview</span>,
              children: (
                <div>
                  <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
                    <Col xs={12} sm={6}>
                      <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: PRIMARY }}>{crop.duration_days ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Duration (days)</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#fa8c16' }}>
                          {crop.min_temp ?? '—'}–{crop.max_temp ?? '—'}°C
                        </div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Temperature</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#1677ff' }}>{crop.avg_yield_per_hectare ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Yield (kg/ha)</div>
                      </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Card size="small" style={{ borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#52c41a' }}>₹{crop.msp ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>MSP</div>
                      </Card>
                    </Col>
                  </Row>

                  <Card size="small" style={{ borderRadius: 8, marginBottom: 12 }} title="Soil & Harvest">
                    <Row gutter={16}>
                      <Col span={12}><Text type="secondary">Soil Type</Text><div style={{ fontWeight: 500 }}>{crop.soil_type || '—'}</div></Col>
                      <Col span={12}><Text type="secondary">pH Range</Text><div style={{ fontWeight: 500 }}>{crop.min_ph ?? '—'} – {crop.max_ph ?? '—'}</div></Col>
                      <Col span={12} style={{ marginTop: 10 }}><Text type="secondary">Harvest Type</Text><div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{(crop.harvest_type || '—').replace(/_/g, ' ')}</div></Col>
                      <Col span={12} style={{ marginTop: 10 }}><Text type="secondary">Harvest Days</Text><div style={{ fontWeight: 500 }}>{crop.harvest_start_day ?? '—'} – {crop.harvest_end_day ?? '—'}</div></Col>
                    </Row>
                  </Card>

                  <Card size="small" style={{ borderRadius: 8, marginBottom: 12 }} title="Nutrients (NPK)">
                    <Row gutter={16}>
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1677ff' }}>{crop.nitrogen ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Nitrogen (N)</div>
                      </Col>
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#fa8c16' }}>{crop.phosphorus ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Phosphorus (P)</div>
                      </Col>
                      <Col span={8} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#52c41a' }}>{crop.potassium ?? '—'}</div>
                        <div style={{ fontSize: 11, color: '#8c8c8c' }}>Potassium (K)</div>
                      </Col>
                    </Row>
                    {parseMicronutrients(crop.micronutrients).length > 0 && (
                      <div style={{ marginTop: 10 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Micronutrients</Text>
                        <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {parseMicronutrients(crop.micronutrients).map((item, i) => (
                            <Tag key={i} color="purple" style={{ textTransform: 'capitalize' }}>{item}</Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </Card>

                  {crop.general_note && (
                    <Card size="small" style={{ borderRadius: 8, background: '#fffbe6', border: '1px solid #ffe58f' }}
                      title={<span><InfoCircleOutlined style={{ marginRight: 5 }} />Note</span>}>
                      <Text>{crop.general_note}</Text>
                    </Card>
                  )}
                </div>
              ),
            },
            {
              key: 'stages',
              label: <span><NodeExpandOutlined /> Stages ({stages.length})</span>,
              children: stages.length === 0 ? (
                <Empty description="No stages defined" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <Collapse accordion defaultActiveKey={stages[0]?.id}>
                  {stages.map((s) => (
                    <Panel
                      key={s.id}
                      header={
                        <Space>
                          <span style={{ fontWeight: 600 }}>{s.stage_name}</span>
                          <Tag style={{ fontSize: 11 }}>Day {s.start_day}–{s.end_day}</Tag>
                          {s.water_requirement && <Tag color="cyan">{s.water_requirement} water</Tag>}
                          {s.nutrient_focus && <Tag color="green">Focus: {s.nutrient_focus}</Tag>}
                        </Space>
                      }
                    >
                      {s.description && <p style={{ color: '#555', marginBottom: 12 }}>{s.description}</p>}
                      {(s.actions || []).length > 0 ? (
                        <Timeline
                          items={(s.actions || []).map((a, i) => ({
                            key: i,
                            color: ACTION_COLOR[a.type] || 'blue',
                            children: (
                              <div style={{ paddingBottom: 4 }}>
                                <Space wrap>
                                  <Tag color={ACTION_COLOR[a.type] || 'default'} style={{ fontSize: 11, textTransform: 'capitalize' }}>{a.type}</Tag>
                                  <span style={{ fontWeight: 600 }}>{a.title}</span>
                                </Space>
                                {a.product_name && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>Product: <strong>{a.product_name}</strong>{a.dose_per_hectare ? ` — ${a.dose_per_hectare}/ha` : ''}</div>}
                                {a.instructions && <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, fontStyle: 'italic' }}>{a.instructions}</div>}
                              </div>
                            ),
                          }))}
                        />
                      ) : (
                        <Text type="secondary">No actions for this stage</Text>
                      )}
                    </Panel>
                  ))}
                </Collapse>
              ),
            },
            {
              key: 'risks',
              label: <span><WarningOutlined /> Risks ({risks.length})</span>,
              children: risks.length === 0 ? (
                <Empty description="No risks defined" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {risks.map((r, i) => (
                    <Card key={r.id || i} size="small" style={{ borderRadius: 8, borderLeft: `4px solid ${r.type === 'pest' ? '#ff4d4f' : r.type === 'disease' ? '#fa541c' : r.type === 'weather' ? '#1677ff' : '#d9d9d9'}` }}>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                        <Tag color={RISK_COLOR[r.type] || 'default'} style={{ textTransform: 'capitalize' }}>{r.type}</Tag>
                        <span style={{ fontWeight: 700 }}>{r.name}</span>
                        {r.risk_stage && <Tag style={{ fontSize: 11 }}>{r.risk_stage}</Tag>}
                      </div>
                      <Row gutter={12}>
                        {r.symptoms  && <Col xs={24} sm={8}><Text type="secondary" style={{ fontSize: 11 }}>Symptoms</Text><div style={{ fontSize: 13 }}>{r.symptoms}</div></Col>}
                        {r.prevention && <Col xs={24} sm={8}><Text type="secondary" style={{ fontSize: 11 }}>Prevention</Text><div style={{ fontSize: 13 }}>{r.prevention}</div></Col>}
                        {r.treatment  && <Col xs={24} sm={8}><Text type="secondary" style={{ fontSize: 11 }}>Treatment</Text><div style={{ fontSize: 13, color: '#1677ff' }}>{r.treatment}</div></Col>}
                      </Row>
                    </Card>
                  ))}
                </div>
              ),
            },
            {
              key: 'advisory',
              label: <span><BulbOutlined /> Advisory</span>,
              children: <AdvisoryPanel cropId={cropId} />,
            },
          ]}
        />
      )}
    </Drawer>
  );
}

// ── Form Drawer ───────────────────────────────────────────────────────────────

function CropFormDrawer({ open, cropId, onClose, onSuccess }) {
  const [form]          = Form.useForm();
  const [saving,        setSaving]        = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [aiLoading,     setAiLoading]     = useState(false);
  const [cropNames,     setCropNames]     = useState([]);
  const [cropNamesLoad, setCropNamesLoad] = useState(false);
  const isEdit = !!cropId;

  const loadCropNames = useCallback(async (search = '') => {
    setCropNamesLoad(true);
    try {
      const res = await getCropNamesApi({ lang: 'en', crop: search });
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      // Deduplicate by common_id — API may return same crop in multiple languages
      const seen = new Set();
      const unique = raw.filter((c) => {
        if (seen.has(c.common_id)) return false;
        seen.add(c.common_id);
        return true;
      });
      setCropNames(unique);
    } catch {
      setCropNames([]);
    } finally {
      setCropNamesLoad(false);
    }
  }, []);

  const handleAiFill = async () => {
    const cropName = form.getFieldValue('name')?.trim();
    if (!cropName) { message.warning('Enter a crop name first'); return; }
    setAiLoading(true);
    try {
      const res = await aiGetCropDataApi(cropName);
      const d = res.data?.data || {};
      form.setFieldsValue({
        ...d,
        season: Array.isArray(d.season) ? d.season.join(',') : (d.season || ''),
        states: Array.isArray(d.states)
          ? d.states
          : (d.states ? d.states.split(',').map((s) => s.trim()).filter(Boolean) : []),
        stages: (d.stages || []).map((s) => ({ ...s, actions: s.actions || [] })),
        risks: d.risks || [],
        micronutrient_list: microArrToList(parseMicronutrients(d.micronutrients)),
      });
      message.success('Form pre-filled with AI data');
    } catch (err) {
      message.error(apiErr(err, 'AI request failed'));
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadCropNames();
  }, [open, loadCropNames]);

  useEffect(() => {
    if (!open) { form.resetFields(); return; }
    if (!isEdit) {
      form.resetFields();
      form.setFieldsValue({ stages: [], risks: [], micronutrient_list: [] });
      return;
    }
    setLoading(true);
    getCropDataByIdApi(cropId)
      .then((res) => {
        const d = res.data?.data || {};
        // API returns: { crop: {...}, stages: [...], risks: [...] }
        const crop = d.crop || {};
        form.setFieldsValue({
          ...crop,
          // season stored as "kharif,rabi" → split to array for multiselect
          season: crop.season || '',
          states: crop.states ? crop.states.split(',').map((s) => s.trim()).filter(Boolean) : [],
          stages: (d.stages || []).map((s) => ({ ...s, actions: s.actions || [] })),
          risks: d.risks || [],
          micronutrient_list: microArrToList(parseMicronutrients(crop.micronutrients)),
        });
      })
      .catch(() => message.error('Failed to load crop data'))
      .finally(() => setLoading(false));
  }, [open, cropId, isEdit, form]);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const { micronutrient_list, states, ...rest } = values;
      const payload = {
        ...rest,
        states: Array.isArray(states) ? states.join(',') : (states || ''),
        micronutrients: microListToArr(micronutrient_list),
      };
      if (isEdit) {
        await updateCropDataApi(cropId, payload);
        message.success('Crop updated successfully');
      } else {
        await createCropDataApi(payload);
        message.success('Crop created successfully');
      }
      onSuccess();
      onClose();
    } catch (err) {
      message.error(apiErr(err, 'Failed to save crop'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <Space>
          <DatabaseOutlined style={{ color: PRIMARY }} />
          {isEdit ? 'Edit Crop Data' : 'Add Crop Data'}
        </Space>
      }
      width={860}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
          >
            {isEdit ? 'Update Crop' : 'Create Crop'}
          </Button>
        </div>
      }
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
      ) : (
        <Form form={form} layout="vertical" size="middle">
          <Collapse defaultActiveKey={['basic', 'climate', 'yield', 'stages', 'risks']} ghost>

            {/* ── Basic Info ── */}
            <Panel key="basic" header={<span style={{ fontWeight: 600 }}>Basic Information</span>}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="common_id" label="Crop (from library)" rules={[{ required: true, message: 'Select a crop' }]}>
                    <Select
                      showSearch
                      placeholder="Search and select crop…"
                      loading={cropNamesLoad}
                      filterOption={false}
                      onSearch={(val) => loadCropNames(val)}
                      onChange={(value, option) => {
                        form.setFieldValue('name', option?.label || '');
                      }}
                      options={cropNames.map((c) => ({ value: c.common_id, label: c.name }))}
                      notFoundContent={cropNamesLoad ? <Spin size="small" /> : 'No crops found'}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="name" label="Crop Name" rules={[{ required: true, message: 'Required' }]}>
                    <Input
                      placeholder="Auto-filled from selection above"
                      suffix={
                        <Tooltip title="Auto-fill entire form using AI">
                          <Button
                            type="link"
                            size="small"
                            icon={<RobotOutlined />}
                            loading={aiLoading}
                            onClick={handleAiFill}
                            style={{ color: '#722ed1', padding: '0 4px', height: 'auto', fontWeight: 600, fontSize: 12 }}
                          >
                            {aiLoading ? 'Loading…' : 'AI Fill'}
                          </Button>
                        </Tooltip>
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="season" label="Season" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="e.g. kharif, rabi, zaid" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item name="states" label="Applicable States">
                    <Select
                      mode="multiple"
                      allowClear
                      showSearch
                      options={INDIAN_STATES}
                      placeholder="Select applicable Indian states"
                      filterOption={(input, option) =>
                        option.label.toLowerCase().includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    name="duration_days"
                    label="Duration (days)"
                    rules={[{ required: true, message: 'Required' }]}
                    extra={<span style={{ fontSize: 11 }}>Single value or range e.g. 120 or 100-150</span>}
                  >
                    <Input placeholder="e.g. 120 or 100-150" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name="harvest_type" label="Harvest Type">
                    <Select options={HARVEST_OPTIONS} placeholder="Select type" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4}>
                  <Form.Item name="sowing_type" label="Sowing Type">
                    <Select
                      allowClear
                      placeholder="Select"
                      options={[{ value: 'seed', label: 'Seed' }, { value: 'plant', label: 'Plant' }]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={4}>
                  <Form.Item
                    name="harvest_start_day"
                    label="Harvest Start (day)"
                    extra={<span style={{ fontSize: 11 }}>e.g. 110 or 100-110</span>}
                  >
                    <Input placeholder="e.g. 110" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={4}>
                  <Form.Item
                    name="harvest_end_day"
                    label="Harvest End (day)"
                    extra={<span style={{ fontSize: 11 }}>e.g. 120 or 110-120</span>}
                  >
                    <Input placeholder="e.g. 120" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="varieties" label="Varieties">
                <Input.TextArea rows={2} placeholder="e.g. HD-2967, PBW-343, Sharbati…" />
              </Form.Item>
              <Form.Item name="general_note" label="General Note">
                <Input.TextArea rows={2} placeholder="Any general notes about this crop..." />
              </Form.Item>
            </Panel>

            {/* ── Climate & Soil ── */}
            <Panel key="climate" header={<span style={{ fontWeight: 600 }}>Climate & Soil</span>}>
              <Row gutter={16}>
                <Col xs={12} sm={6}>
                  <Form.Item name="min_temp" label="Min Temp (°C)">
                    <InputNumber style={{ width: '100%' }} placeholder="e.g. 20" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="max_temp" label="Max Temp (°C)">
                    <InputNumber style={{ width: '100%' }} placeholder="e.g. 35" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="min_ph" label="Min pH">
                    <InputNumber min={0} max={14} step={0.1} style={{ width: '100%' }} placeholder="e.g. 6.0" />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Item name="max_ph" label="Max pH">
                    <InputNumber min={0} max={14} step={0.1} style={{ width: '100%' }} placeholder="e.g. 7.5" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="soil_type" label="Soil Type">
                <Input placeholder="e.g. Sandy Loam, Clay, Alluvial" />
              </Form.Item>
            </Panel>

            {/* ── Yield & Nutrients ── */}
            <Panel key="yield" header={<span style={{ fontWeight: 600 }}>Yield & Nutrients</span>}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="avg_yield_per_hectare"
                    label="Avg Yield (ton/ha)"
                    extra={<span style={{ fontSize: 11 }}>Single value or range e.g. 5 or 4-6</span>}
                  >
                    <Input placeholder="e.g. 5 or 4-6" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="msp" label="MSP (₹/quintal)">
                    <InputNumber min={0} prefix="₹" style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={8}>
                  <Form.Item name="nitrogen" label="Nitrogen (N)">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="kg/ha" />
                  </Form.Item>
                </Col>
                <Col xs={8}>
                  <Form.Item name="phosphorus" label="Phosphorus (P)">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="kg/ha" />
                  </Form.Item>
                </Col>
                <Col xs={8}>
                  <Form.Item name="potassium" label="Potassium (K)">
                    <InputNumber min={0} style={{ width: '100%' }} addonAfter="kg/ha" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col xs={24} sm={12}>
                  <Form.Item name="basal_dose" label="Basal Dose">
                    <Input placeholder="e.g. NPK 12:32:16" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="top_dressing" label="Top Dressing">
                    <Input placeholder="e.g. Urea 50 kg/ha at 30 DAS" />
                  </Form.Item>
                </Col>
              </Row>

              <Divider style={{ margin: '8px 0 12px' }}>Micronutrients</Divider>
              <Form.List name="micronutrient_list">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Row key={field.key} gutter={10} style={{ marginBottom: 8 }} align="middle">
                        <Col flex="1">
                          <Form.Item name={[field.name, 'value']} noStyle>
                            <Input placeholder="e.g. Zinc, Boron, Iron" />
                          </Form.Item>
                        </Col>
                        <Col>
                          <Button danger icon={<MinusCircleOutlined />} size="small" onClick={() => remove(field.name)} />
                        </Col>
                      </Row>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ value: '' })} size="small">
                      Add Micronutrient
                    </Button>
                  </>
                )}
              </Form.List>
            </Panel>

            {/* ── Growth Stages ── */}
            <Panel key="stages" header={<span style={{ fontWeight: 600 }}>Growth Stages</span>}>
              <Form.List name="stages">
                {(stageFields, { add: addStage, remove: removeStage }) => (
                  <>
                    {stageFields.map((sf, si) => (
                      <Card
                        key={sf.key}
                        size="small"
                        style={{ marginBottom: 12, border: '1px solid #d9e8d9' }}
                        styles={{ header: { background: '#f6fff6' } }}
                        title={<span style={{ color: PRIMARY, fontWeight: 600 }}>Stage {si + 1}</span>}
                        extra={
                          <Button danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeStage(sf.name)}>
                            Remove
                          </Button>
                        }
                      >
                        <Row gutter={12}>
                          <Col xs={24} sm={12}>
                            <Form.Item name={[sf.name, 'stage_name']} label="Stage Name" rules={[{ required: true, message: 'Required' }]}>
                              <Input placeholder="e.g. Germination, Vegetative" />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item name={[sf.name, 'start_day']} label="Start Day" rules={[{ required: true, message: 'Required' }]}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col xs={12} sm={6}>
                            <Form.Item name={[sf.name, 'end_day']} label="End Day" rules={[{ required: true, message: 'Required' }]}>
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={12}>
                          <Col xs={24} sm={12}>
                            <Form.Item name={[sf.name, 'water_requirement']} label="Water Requirement">
                              <Select options={WATER_REQ_OPTIONS} placeholder="Select" allowClear />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12}>
                            <Form.Item name={[sf.name, 'nutrient_focus']} label="Nutrient Focus">
                              <Input placeholder="e.g. N, P, K, NPK" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item name={[sf.name, 'description']} label="Description">
                          <Input.TextArea rows={2} placeholder="Stage description..." />
                        </Form.Item>

                        <Divider style={{ margin: '6px 0 10px', fontSize: 12 }}>Actions</Divider>
                        <Form.List name={[sf.name, 'actions']}>
                          {(actionFields, { add: addAction, remove: removeAction }) => (
                            <>
                              {actionFields.map((af) => (
                                <Card key={af.key} size="small" style={{ marginBottom: 8, background: '#fafafa', border: '1px solid #f0f0f0' }}>
                                  <Row gutter={10} align="top">
                                    <Col xs={24} sm={6}>
                                      <Form.Item name={[af.name, 'type']} label="Type">
                                        <Select options={ACTION_TYPE_OPTIONS} placeholder="Type" size="small" />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={10}>
                                      <Form.Item name={[af.name, 'title']} label="Title">
                                        <Input placeholder="e.g. Light irrigation" size="small" />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={8}>
                                      <Form.Item name={[af.name, 'product_name']} label="Product Name">
                                        <Input placeholder="Product (optional)" size="small" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  <Row gutter={10}>
                                    <Col xs={24} sm={8}>
                                      <Form.Item name={[af.name, 'dose_per_hectare']} label="Dose/Hectare">
                                        <Input placeholder="e.g. 50kg" size="small" />
                                      </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={16}>
                                      <Form.Item name={[af.name, 'instructions']} label="Instructions">
                                        <Input placeholder="Instructions (optional)" size="small" />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                  <Button danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeAction(af.name)}>
                                    Remove Action
                                  </Button>
                                </Card>
                              ))}
                              <Button
                                type="dashed" size="small" icon={<PlusOutlined />}
                                onClick={() => addAction({ type: 'irrigation', title: '', product_name: null, dose_per_hectare: null, instructions: '' })}
                                style={{ marginTop: 4 }}
                              >
                                Add Action
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Card>
                    ))}
                    <Button
                      type="dashed" icon={<PlusOutlined />} block
                      onClick={() => addStage({ stage_name: '', start_day: 0, end_day: 0, actions: [] })}
                    >
                      Add Stage
                    </Button>
                  </>
                )}
              </Form.List>
            </Panel>

            {/* ── Risks ── */}
            <Panel key="risks" header={<span style={{ fontWeight: 600 }}>Risks (Pests & Diseases)</span>}>
              <Form.List name="risks">
                {(riskFields, { add: addRisk, remove: removeRisk }) => (
                  <>
                    {riskFields.map((rf) => (
                      <Card key={rf.key} size="small" style={{ marginBottom: 10, border: '1px solid #ffe0e0' }} styles={{ header: { background: '#fff2f0' } }}
                        title={<span style={{ color: '#cf1322', fontSize: 12 }}>Risk Entry</span>}
                        extra={<Button danger size="small" icon={<MinusCircleOutlined />} onClick={() => removeRisk(rf.name)} />}>
                        <Row gutter={12}>
                          <Col xs={24} sm={6}>
                            <Form.Item name={[rf.name, 'type']} label="Type">
                              <Select options={RISK_TYPE_OPTIONS} placeholder="Type" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={9}>
                            <Form.Item name={[rf.name, 'name']} label="Name">
                              <Input placeholder="e.g. Aphids, Rust, Drought" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={9}>
                            <Form.Item name={[rf.name, 'risk_stage']} label="Affects Stage">
                              <Input placeholder="e.g. Vegetative, All stages" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={12}>
                          <Col xs={24} sm={8}>
                            <Form.Item name={[rf.name, 'symptoms']} label="Symptoms">
                              <Input.TextArea rows={2} placeholder="Visible symptoms..." />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Form.Item name={[rf.name, 'prevention']} label="Prevention">
                              <Input.TextArea rows={2} placeholder="Preventive measures..." />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={8}>
                            <Form.Item name={[rf.name, 'treatment']} label="Treatment">
                              <Input.TextArea rows={2} placeholder="Treatment / chemical..." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                    <Button
                      type="dashed" icon={<PlusOutlined />} block
                      onClick={() => addRisk({ type: 'pest', name: '', risk_stage: '', symptoms: '', prevention: '', treatment: '' })}
                    >
                      Add Risk
                    </Button>
                  </>
                )}
              </Form.List>
            </Panel>

          </Collapse>
        </Form>
      )}
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminCropData() {
  const [crops,         setCrops]         = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [seasonFilter,  setSeasonFilter]  = useState('');
  const [searchText,    setSearchText]    = useState('');

  const [formOpen,      setFormOpen]      = useState(false);
  const [editCropId,    setEditCropId]    = useState(null);
  const [detailOpen,    setDetailOpen]    = useState(false);
  const [detailCropId,  setDetailCropId]  = useState(null);

  const fetchCrops = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (seasonFilter) params.season = seasonFilter;
      const res = await getCropDataListApi(params);
      const raw = res.data?.data || res.data || [];
      setCrops(Array.isArray(raw) ? raw : []);
    } catch (err) {
      message.error(apiErr(err, 'Failed to load crops'));
    } finally {
      setLoading(false);
    }
  }, [seasonFilter]);

  useEffect(() => { fetchCrops(); }, [fetchCrops]);

  const handleDelete = async (id) => {
    try {
      await deleteCropDataApi(id);
      message.success('Crop deleted');
      fetchCrops();
    } catch (err) {
      message.error(apiErr(err, 'Failed to delete crop'));
    }
  };

  const handleEdit = (id) => {
    setEditCropId(id);
    setFormOpen(true);
  };

  const filteredCrops = searchText
    ? crops.filter((c) => (c.name || '').toLowerCase().includes(searchText.toLowerCase()))
    : crops;

  const columns = [
    {
      title: 'Crop',
      key: 'crop',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#f0faf0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
            🌱
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>ID: {r.id}</div>
          </div>
        </div>
      ),
      width: 180,
    },
    {
      title: 'Season',
      dataIndex: 'season',
      width: 100,
      render: (v) => v ? (
        <Tag color={SEASON_COLOR[v] || 'default'} style={{ textTransform: 'capitalize' }}>{v}</Tag>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Duration',
      dataIndex: 'duration_days',
      width: 110,
      render: (v) => v ? <span style={{ fontSize: 13 }}>{v} days</span> : <Text type="secondary">—</Text>,
      sorter: (a, b) => (a.duration_days ?? 0) - (b.duration_days ?? 0),
    },
    {
      title: 'Temp Range',
      key: 'temp',
      width: 130,
      render: (_, r) => (r.min_temp != null || r.max_temp != null) ? (
        <span style={{ fontSize: 13 }}>{r.min_temp ?? '?'}–{r.max_temp ?? '?'}°C</span>
      ) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Yield (kg/ha)',
      dataIndex: 'avg_yield_per_hectare',
      width: 120,
      render: (v) => v ? <span style={{ fontSize: 13, color: PRIMARY, fontWeight: 600 }}>{Number(v).toLocaleString('en-IN')}</span> : <Text type="secondary">—</Text>,
      sorter: (a, b) => (a.avg_yield_per_hectare ?? 0) - (b.avg_yield_per_hectare ?? 0),
    },
    {
      title: 'MSP',
      dataIndex: 'msp',
      width: 110,
      render: (v) => v ? <span style={{ fontSize: 13, fontWeight: 600 }}>₹{Number(v).toLocaleString('en-IN')}</span> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Stages',
      dataIndex: 'stages_count',
      width: 80,
      render: (v) => v != null ? <Badge count={v} color={PRIMARY} /> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => { setDetailCropId(r.id); setDetailOpen(true); }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r.id)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this crop?"
              description="All stages, actions and risks will be deleted."
              onConfirm={() => handleDelete(r.id)}
              okButtonProps={{ danger: true }}
              okText="Delete"
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: PRIMARY }}>
          <DatabaseOutlined style={{ marginRight: 8 }} />
          Crop Data
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCrops} loading={loading} />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: PRIMARY, borderColor: PRIMARY }}
            onClick={() => { setEditCropId(null); setFormOpen(true); }}
          >
            Add Crop
          </Button>
        </Space>
      </div>

      {/* Filters */}
      <Card style={{ borderRadius: 10, marginBottom: 16 }} styles={{ body: { padding: '12px 14px' } }}>
        <Space wrap>
          <Input
            placeholder="Search by name…"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Select
            value={seasonFilter || undefined}
            onChange={(v) => setSeasonFilter(v || '')}
            placeholder="All Seasons"
            allowClear
            style={{ width: 140 }}
            options={SEASON_OPTIONS}
          />
          {(searchText || seasonFilter) && (
            <Button onClick={() => { setSearchText(''); setSeasonFilter(''); }}>Clear</Button>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>{filteredCrops.length} crops</Text>
        </Space>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 10 }} styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={filteredCrops}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          size="small"
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t) => `${t} crops`,
            defaultPageSize: 20,
          }}
          locale={{ emptyText: (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No crops added yet">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ background: PRIMARY, borderColor: PRIMARY }}
                onClick={() => { setEditCropId(null); setFormOpen(true); }}
              >
                Add First Crop
              </Button>
            </Empty>
          )}}
        />
      </Card>

      {/* Form Drawer */}
      <CropFormDrawer
        open={formOpen}
        cropId={editCropId}
        onClose={() => { setFormOpen(false); setEditCropId(null); }}
        onSuccess={fetchCrops}
      />

      {/* Detail Drawer */}
      <CropDetailDrawer
        open={detailOpen}
        cropId={detailCropId}
        onClose={() => { setDetailOpen(false); setDetailCropId(null); }}
        onEdit={(id) => { setEditCropId(id); setFormOpen(true); }}
      />
    </div>
  );
}
